<template>
  <transition :name="modalAnimationType">
    <div class="modal" v-if="show" @click="backdropClicked" ref="modal">
      <div class="body" :class="{'no-padding': !padding}">
        <span class="af af-close close" :class="[closePosition]" @click="onCloseClicked" v-if="showClose"></span>
        <!-- @slot Your content in the modal -->
        <slot></slot>
      </div>
    </div>
  </transition>
</template>

<script>
import { nextTick } from 'vue';

export default {
  name: 'Modal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    /**
     * Boolean whether to show close icon or not
     */
    showClose: Boolean,
    closeOnEscape: {
      type: Boolean,
      default: false
    },
    closeOnBackdropClick:{
      type: Boolean,
      default: true
    },
    /**
     * Close icon position in the modal
     * @values right, left
     */
    padding:{
      type: Boolean,
      default: true
    },
    closePosition: {
      type: String,
      default: 'right',
      validator(value) {
        return ['right', 'left'].indexOf(value) !== -1;
      },
    },
    /**
     * Type of animation
     * @values fade, slide, none
     */
    animationType: {
      type: String,
      default: 'fade',
      validator(value) {
        return ['fade', 'slide', 'none'].indexOf(value) !== -1;
      },
    },
  },
  mounted() {
    document.addEventListener('keyup', this.onKeyUp);
  },
  unmounted() {
    document.removeEventListener('keyup', this.onKeyUp);
  },
  methods: {
    onCloseClicked() {
      /**
       * Event to update show props
       * @property {boolean} value to close the modal
       */
      this.$emit('update:show', false);
    },

    onKeyUp(event) {
      if (event.key == 'Escape') {
        if(this.closeOnEscape){
          this.onCloseClicked();
        }
      }
    },
    backdropClicked(e){
      if(this.closeOnBackdropClick && e.target === this.$refs.modal){
        this.onCloseClicked();
      }
    }
  },
  computed: {
    modalAnimationType() {
      return `modal-${this.animationType}`;
    },
  },
  watch: {
    show: {
      handler(value) {
        if (value) {
          nextTick(() => {
            const html = document.querySelector('html');
            html?.classList.add('overflow-hidden-only');
          });
        } else {
            const html = document.querySelector('html');
            html?.classList.remove('overflow-hidden-only');
        }
      },
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 120ms ease-out;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: transform 220ms ease-out;
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  transform: translateY(100%);
}

.modal {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  background: var(--overlay-color);

  --padding: 36px;

  &.active {
    padding-right: 17px;
  }

  .body {
    @mixin overlay;

    background: white;
    position: relative;
    padding: var(--padding);
    max-height: 100%;
    box-sizing: border-box;
    overflow: auto;

    &.no-padding{
      padding: 0;
    }

    .close {
      position: absolute;
      top: var(--space-3);
      right: var(--space-3);
      padding: var(--space-2) var(--space-3);
      z-index: 3;
      cursor: pointer;

      &.right {
        right: 8px;
      }

      &.left {
        left: 26px;
      }
    }

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      width: 100%;
    }

    @media (min-width: $mobile-breakpoint) {
      max-width: 80%;
    }

    @media (min-width: $tablet-breakpoint) {
      max-width: 75%;
    }

    @media (min-width: $desktop-breakpoint) {
      max-width: 1000px;
    }
  }
}
</style>
